import React from 'react';
import { LocationCardProps } from '@/components/card/types';
import { BaseImage } from '@/components/baseImage/BaseImage';
import { Link } from '@/components/link/Link';
import clsx from 'clsx';
import { WithContext, LocalBusiness } from 'schema-dts';
import Script from 'next/script';

// Parse address from string like: "Kornhausstrasse 25, 9000 St. Gallen"
function parseAddress(text: string) {
  const parts = text.split(',');
  if (parts.length < 2) return null;

  const streetAddress = parts[0].trim();
  const cityPart = parts[1].trim();
  const match = cityPart.match(/^(\d{4})\s+(.+)$/);

  if (!match) return null;

  return {
    streetAddress,
    postalCode: match[1],
    addressLocality: match[2],
  };
}
import { renameDomProps } from '@/utils/sanitizeDomProps';

const LocationCard: React.FC<LocationCardProps> = React.memo(
  ({ image, title, links, className, ...rest }) => {
    // Extract email, phone, and address link from links
    const email = links.find((link) => link.title?.includes('@'))?.title;
    const telephone = links.find((link) => link.title?.includes('+'))?.title;
    const addressLink = links.find(
      (link) =>
        link.title &&
        link.title.includes(',') &&
        /\d{4}\s+\w+/.test(link.title),
    );
    const hasMap = addressLink?.href;
    const isProduction = process.env.NEXT_PUBLIC_ENV === 'production';

    const schema: WithContext<LocalBusiness> = {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: `smzh ag – ${title}`,
      url: 'https://smzh.ch/de/kontakt/',
      image: image?.image?.url
        ? `https://cms.smzh.ch${image.image.url}`
        : undefined,
      email,
      telephone,
      ...(parseAddress(addressLink?.title || '') && {
        address: {
          '@type': 'PostalAddress',
          streetAddress: parseAddress(addressLink?.title || '')?.streetAddress,
          postalCode: parseAddress(addressLink?.title || '')?.postalCode,
          addressLocality: parseAddress(addressLink?.title || '')
            ?.addressLocality,
          addressRegion: 'SG',
          addressCountry: 'CH',
        },
      }),
      hasMap: hasMap,
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '17:00',
        },
      ],
      priceRange:
        'Preisgestaltung individuell – abhängig von persönlicher Beratung und Finanzbedarf',
    };

    const renamedProps = renameDomProps(rest);
    return (
      <div
        className={clsx(
          'location-card flex flex-col overflow-hidden rounded-xl border border-neutral-50 text-brand-700',
          className,
        )}
        {...renamedProps}
      >
        {image && (
          <BaseImage
            {...image}
            className="h-80"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        )}
        <div className="flex flex-col gap-4 self-stretch bg-white p-6">
          {title && <h3 className="text-xl font-medium">{title}</h3>}
          {links.length > 0 && (
            <ul className="card-links flex list-none flex-col gap-2">
              {links.map((link, index) => (
                <Link key={index} {...link}>
                  {link.title}
                </Link>
              ))}
            </ul>
          )}
        </div>

        {/* Add JSON-LD for schema ..note(replace() is used to avoid the script tags inject)*/}
        {isProduction && (
          <Script
            type="application/ld+json"
            id="locationcard-schema"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
            }}
          />
        )}
      </div>
    );
  },
);

LocationCard.displayName = 'LocationCard';

export default LocationCard;
